import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import {
  createProductCheckout,
  getProduct,
  listProductOptions,
  listProducts,
  listPromoCodes,
  type Api,
} from "api-client";
import { type TFunction } from "i18next";

import { getProductErrorMessage } from "src/helpers/productErrorMessage";

export type Products = Api.ListProducts.Output.Self;
export type Product = Api.ListProducts.Output.CurrentPage;
export type ProductPrice = Api.ListProducts.Output.Price;
export type ProductsTab = "active" | "archived";
export type ProductStatus = Product["status"];
export type ProductSales = Api.GetProductSales.Output.Self;
export type ProductPayment = Api.GetProductSales.Output.CurrentPage;
export type PromoCode = Api.ListPromoCodes.Output.PromoCode;

export const PRODUCTS_PAGE_SIZE = 10;

interface ProductsSearch {
  tab?: ProductsTab;
  q?: string;
  p?: number;
  ps?: number;
}

interface ProductsListQuery {
  tab: ProductsTab;
  q: string;
  pageIndex: number;
  pageSize: number;
}

export const productKeys = {
  all: ["products"] as const,
  list: () => {
    return [...productKeys.all, "list"] as const;
  },
  page: (query: ProductsListQuery) => {
    return [...productKeys.list(), query] as const;
  },
  options: () => {
    return [...productKeys.all, "options"] as const;
  },
  detail: (productId: string) => {
    return [...productKeys.all, "detail", productId] as const;
  },
  promoCodes: () => {
    return [...productKeys.all, "promo-codes"] as const;
  },
  checkout: (productId: string, priceId: string) => {
    return [...productKeys.all, "checkout", productId, priceId] as const;
  },
};

export function productsQueryOptions(search: ProductsSearch) {
  const query: ProductsListQuery = {
    tab: search.tab ?? "active",
    q: search.q ?? "",
    pageIndex: search.p ?? 0,
    pageSize: search.ps ?? PRODUCTS_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: productKeys.page(query),
    queryFn: () => {
      return listProducts({ query }).then((res) => {
        return res.data.data;
      });
    },
  });
}

export function productOptionsQueryOptions() {
  return queryOptions({
    queryKey: productKeys.options(),
    queryFn: () => {
      return listProductOptions().then((res) => {
        return res.data.data;
      });
    },
  });
}

export function productQueryOptions(productId: string) {
  return queryOptions({
    queryKey: productKeys.detail(productId),
    queryFn: () => {
      return getProduct({ path: { productId } }).then((res) => {
        return res.data.data;
      });
    },
  });
}

export function promoCodesQueryOptions() {
  return infiniteQueryOptions({
    queryKey: productKeys.promoCodes(),
    queryFn: (context) => {
      return listPromoCodes({
        query: { cursor: context.pageParam ?? undefined },
      }).then((res) => {
        return res.data.data;
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor;
    },
  });
}

export function productCheckoutQueryOptions(
  t: TFunction,
  productId: string,
  priceId: string,
) {
  return queryOptions({
    queryKey: productKeys.checkout(productId, priceId),
    queryFn: async () => {
      const response = await createProductCheckout({
        path: { productId },
        body: { priceId },
        throwOnError: false,
      });

      if (response.error) {
        throw new Error(getProductErrorMessage(t, response.error));
      }

      return response.data.data;
    },
    gcTime: 0,
    retry: false,
  });
}
